import React, { useEffect, useState } from 'react';

type MedalTier = 'standard' | 'bronze' | 'silver' | 'gold' | 'master';

interface MedalCelebrationProps {
  open: boolean;
  medalTitle: string;
  medalImage: string;
  // Optional — when provided, masks the placeholder text baked into the
  // Figma PNG and renders the real DB-driven quest description + progress
  // instead. Tier drives the panel colour so it blends with the card.
  medalDescription?: string;
  medalTier?: MedalTier;
  medalProgress?: number;
  medalCriteriaValue?: number | null;
  onClose: () => void;
  onGoToMedals: () => void;
}

// Tier panel colours dropped — açıklama doğrudan görselin üstüne gradient
// backdrop ile yazılıyor.

// Simple confetti particles
const CONFETTI_COLORS = ['#3973bf', '#e5599b', '#ffda09', '#59e56e', '#6d79ff', '#f1972f'];

const ConfettiPiece: React.FC<{ index: number }> = ({ index }) => {
  const color = CONFETTI_COLORS[index % CONFETTI_COLORS.length];
  const left = Math.random() * 100;
  const delay = Math.random() * 0.5;
  const duration = 1.5 + Math.random() * 1;
  const size = 6 + Math.random() * 6;
  const rotation = Math.random() * 360;

  return (
    <div
      className="absolute rounded-sm"
      style={{
        left: `${left}%`,
        top: -10,
        width: size,
        height: size * 0.6,
        background: color,
        transform: `rotate(${rotation}deg)`,
        animation: `confettiFall ${duration}s ease-in ${delay}s forwards`,
      }}
    />
  );
};

const MedalCelebration: React.FC<MedalCelebrationProps> = ({
  open,
  medalTitle,
  medalImage,
  medalDescription,
  medalTier: _medalTier = 'standard',
  medalProgress,
  medalCriteriaValue,
  onClose,
  onGoToMedals,
}) => {
  // Progress bar artık görselin içinde baked olarak geliyor — kendi
  // progress hesaplamamızı kaldırdık.
  void medalProgress; void medalCriteriaValue;
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => setShowContent(true), 100);
      return () => clearTimeout(t);
    }
    setShowContent(false);
  }, [open]);

  if (!open) return null;

  return (
    <>
      {/* Confetti keyframes */}
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(500px) rotate(720deg); opacity: 0; }
        }
        @keyframes medalBounce {
          0% { transform: scale(0.3) rotate(-10deg); opacity: 0; }
          50% { transform: scale(1.15) rotate(3deg); opacity: 1; }
          70% { transform: scale(0.95) rotate(-2deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes fadeSlideUp {
          0% { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
      `}</style>

      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

        {/* Popup */}
        <div
          className="relative bg-white rounded-[24px] w-full max-w-[480px] p-8 text-center overflow-hidden"
          style={{ boxShadow: '0px 8px 32px rgba(0,0,0,0.2)' }}
        >
          {/* Confetti */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 30 }, (_, i) => (
              <ConfettiPiece key={i} index={i} />
            ))}
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-[36px] h-[36px] rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#2b2b2b" strokeWidth="2.5" strokeLinecap="round">
              <line x1="3" y1="3" x2="13" y2="13" />
              <line x1="13" y1="3" x2="3" y2="13" />
            </svg>
          </button>

          {/* Açıklama görselin üst kısmının üstünde gradient backdrop ile
              gösteriliyor — baked başlık/progress alanını kapatmayacak şekilde
              üst yarıya yerleşir. */}
          <div
            className="mx-auto mb-4 relative overflow-hidden rounded-[16px]"
            style={{
              width: 260, height: 400,
              animation: showContent ? 'medalBounce 0.6s ease-out 0.1s forwards' : undefined, opacity: 0,
              boxShadow: '0px 8px 32px rgba(0,0,0,0.3)',
            }}
          >
            <img
              src={medalImage}
              alt={medalTitle}
              className="w-full h-full object-cover"
            />
            {medalDescription && (
              // Title %46-62 + progress group %84+ baked alanlar — açıklama
              // bunların arasındaki %62-84 bandında konumlanır.
              <div
                className="absolute inset-x-0 flex items-center justify-center pointer-events-none"
                style={{
                  top: '62%',
                  bottom: '20%',
                  padding: '0 18px',
                  textAlign: 'center',
                }}
              >
                <p
                  style={{
                    fontFamily: 'Inter', fontSize: 13, fontWeight: 600, lineHeight: 1.25,
                    color: '#ffffff',
                    textShadow: '0 1px 2px rgba(0,0,0,0.85), 0 0 4px rgba(0,0,0,0.6)',
                    margin: 0,
                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}
                >
                  {medalDescription}
                </p>
              </div>
            )}

            {/* Cover baked "1/1" + progress bar with real DB values. */}
            {(() => {
              const TIER_COVER: Record<MedalTier, { bg: string; text: string; track: string; fill: string }> = {
                standard: { bg: '#fafafa', text: '#2b2b2b', track: '#1e1e1e', fill: '#c7b294' },
                bronze:   { bg: '#867a64', text: '#fafafa', track: '#1e1e1e', fill: '#fafafa' },
                silver:   { bg: '#d2d0d1', text: '#2b2b2b', track: '#737373', fill: '#fafafa' },
                gold:     { bg: '#e5b93f', text: '#2b2b2b', track: '#3a2b0a', fill: '#fafafa' },
                master:   { bg: '#3d3d3d', text: '#fafafa', track: '#1f1f1f', fill: '#fafafa' },
              };
              const ts = TIER_COVER[_medalTier as MedalTier] ?? TIER_COVER.standard;
              const total = medalCriteriaValue ?? 0;
              const done = medalProgress ?? 0;
              const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : (done > 0 ? 100 : 0);
              return (
                <div
                  className="absolute inset-x-0 flex flex-col justify-center pointer-events-none"
                  style={{ bottom: 0, height: '20%', background: ts.bg, padding: '0 18px' }}
                >
                  <p style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 600, color: ts.text, textAlign: 'center', lineHeight: 1, margin: '0 0 5px' }}>
                    {total > 0 ? `${done}/${total}` : (done > 0 ? '✓ Tamamlandı' : '')}
                  </p>
                  <div style={{ height: 5, background: ts.track, borderRadius: 2.5, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: ts.fill, transition: 'width 0.4s ease' }} />
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Congrats */}
          <p
            style={{
              fontFamily: 'Inter', fontSize: 18, fontWeight: 700, color: '#3973bf', marginBottom: 4,
              animation: showContent ? 'fadeSlideUp 0.5s ease-out 0.35s forwards' : undefined, opacity: 0,
            }}
          >
            Harikasın!
          </p>

          <p
            style={{
              fontFamily: 'Inter', fontSize: 12, fontWeight: 500, color: '#2b2b2b', marginBottom: 16, lineHeight: '1.5',
              animation: showContent ? 'fadeSlideUp 0.5s ease-out 0.45s forwards' : undefined, opacity: 0,
            }}
          >
            Bu senin başarın, sonuna kadar hak ettin.<br />
            Şimdi vitrinini güncelleyip madalyanın tadını çıkarabilirsin üstat!
          </p>

          {/* Button */}
          <div
            style={{ animation: showContent ? 'fadeSlideUp 0.5s ease-out 0.55s forwards' : undefined, opacity: 0 }}
          >
            <button
              onClick={onGoToMedals}
              className="rounded-[12px] px-8 py-3 hover:opacity-90 transition-opacity"
              style={{ background: '#e5599b' }}
            >
              <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#fafafa' }}>Madalyalarıma Git</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MedalCelebration;
